// src/components/CoachSignup.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import imageCompression from "browser-image-compression";
import Cropper from "react-easy-crop";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { motion, AnimatePresence } from "framer-motion";
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Button,
  InputAdornment,
  Snackbar,
  Alert,
  IconButton,
  Box,
  Stepper,
  Step,
  StepLabel,
  Tooltip,
} from "@mui/material";
import { FaUser, FaEnvelope, FaLock, FaMoneyBillWave } from "react-icons/fa";
import { FiEye, FiEyeOff, FiRefreshCw, FiWifiOff } from "react-icons/fi";
import { API_BASE } from "../config/api";

/* ---------- crop helper ---------- */
export async function getCroppedImg(
  imageSrc,
  pixelCrop,
  outputType = "image/png",
  quality = 0.92
) {
  if (!imageSrc || !pixelCrop) throw new Error("Missing imageSrc or crop");
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = imageSrc;
  });
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D canvas context not available");
  const { x, y, width, height } = pixelCrop;
  canvas.width = Math.max(1, Math.floor(width));
  canvas.height = Math.max(1, Math.floor(height));
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    image,
    Math.max(0, x),
    Math.max(0, y),
    Math.max(1, width),
    Math.max(1, height),
    0,
    0,
    canvas.width,
    canvas.height
  );
  return canvas.toDataURL(outputType, quality);
}

/* ---------- axios ---------- */
const api = axios.create({ baseURL: API_BASE, timeout: 45000 });

const toArray = (v) =>
  Array.isArray(v)
    ? v
    : v?.items || v?.data || (v && typeof v === "object" ? Object.values(v) : []);
const arr = (v) => (Array.isArray(v) ? v : toArray(v));

async function fetchWithRetry(url, { cacheKey, tries = 2 } = {}) {
  let lastErr;
  for (let i = 0; i < tries; i++) {
    try {
      const res = await api.get(url);
      if (cacheKey) localStorage.setItem(cacheKey, JSON.stringify(res.data));
      return res.data;
    } catch (err) {
      lastErr = err;
      await new Promise((r) => setTimeout(r, 400 * (i + 1)));
    }
  }
  if (cacheKey) {
    const cached = localStorage.getItem(cacheKey);
    if (cached) return JSON.parse(cached);
  }
  const e = new Error("Network Error: Failed to reach API and no cache is available");
  e.cause = lastErr;
  throw e;
}

/* ---------- OTP helpers ---------- */
const WEB_BASE_URL =
  (typeof window !== "undefined" && window.location && window.location.origin) ||
  "https://ptfindernow.com";

async function requestOtp(to, expiresMinutes = 10) {
  const email = String(to || "").trim().toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
    throw new Error("Please enter a valid email address.");
  const res = await api.post("/auth/request-otp", { email, expiresMinutes });
  return res.data;
}
async function verifyOtp(email, code) {
  const res = await api.post("/auth/verify-otp", { email, code });
  return res.data;
}

/* ---------- keys ---------- */
const PROOF_KEY = "ptn_email_proof";
const ACCOUNT_CACHE_KEY = "ptn_signup_account";
const ACCOUNT_CACHE_TTL_MS = 2 * 60 * 60 * 1000; // 2h

/* ---------- schema ---------- */
const idField = z.preprocess((v) => (v == null ? "" : String(v)), z.string().min(1, "Required"));

const coachSchema = z
  .object({
    fullName: z.string().min(2, "Please enter your full name"),
    email: z.string().email("Enter a valid email"),
    password: z
      .string()
      .min(8, "Min 8 characters")
      .regex(/[A-Z]/, "Include an uppercase letter")
      .regex(/[0-9]/, "Include a number"),
    confirmPassword: z.string(),
    countryCode: z.string().min(1, "Select code"),
    phoneNumber: z.string().regex(/^\d{9}$/, "Phone must be exactly 9 digits"),
    gender: z.enum(["Male", "Female"], { required_error: "Select gender" }),
    price: z.string().optional(),
    description: z.string().min(20, "Tell clients about you (20+ chars)").max(1000),
    countryId: idField,
    cityId: idField,
    areaId: idField,
    categoryId: idField,
    specialityId: idField,

    // ✅ ONE checkbox for BOTH Terms + Privacy
    acceptPolicies: z.boolean().refine((v) => v, {
      message: "You must accept the Terms & Privacy Policy",
    }),
  })
  .refine((d) => d.password === d.confirmPassword, {
    path: ["confirmPassword"],
    message: "Passwords do not match",
  });

/* =================================================== */
export default function CoachSignup() {
  const [categories, setCategories] = useState([]);
  const [specialities, setSpecialities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [areas, setAreas] = useState([]);

  const [loading, setLoading] = useState({
    categories: false,
    countries: false,
    cities: false,
    areas: false,
    specialities: false,
    submit: false,
    resend: false,
    sendCode: false,
  });

  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });
  const [offline, setOffline] = useState(false);

  // ORDER: Account → Verify code → Profile → Register → Done
  const steps = ["Account", "Verify code", "Profile", "Register", "Done"];
  const [activeStep, setActiveStep] = useState(0);

  // crop state
  const [imagePreview, setImagePreview] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null); // objectURL for preview
  const [croppedBlob, setCroppedBlob] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // email/otp/proof
  const [emailStatus, setEmailStatus] = useState("idle"); // idle | checking | taken | free
  const emailTimer = useRef(null);
  const [pendingEmail, setPendingEmail] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isVerified, setIsVerified] = useState(false);
  const [otp, setOtp] = useState("");

  useEffect(() => {
    if (!resendCooldown) return;
    const t = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  const countryCodes = useMemo(() => [{ code: "+971", label: "UAE" }], []);
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(coachSchema),
    shouldUnregister: false,
    defaultValues: {
      fullName: "",
      email: "",
      password: "",
      confirmPassword: "",
      countryCode: "+971",
      phoneNumber: "",
      gender: "",
      price: "",
      description: "",
      countryId: "",
      cityId: "",
      areaId: "",
      categoryId: "",
      specialityId: "",
      acceptPolicies: false,
    },
    mode: "onBlur",
  });

  const password = watch("password");
  const categoryId = watch("categoryId");
  const countryId = watch("countryId");
  const cityId = watch("cityId");
  const email = watch("email");

  // ✅ controlled values for MUI Selects
  const countryCodeVal = watch("countryCode");
  const genderVal = watch("gender");
  const countryIdVal = watch("countryId");
  const cityIdVal = watch("cityId");
  const areaIdVal = watch("areaId");
  const categoryIdVal = watch("categoryId");
  const specialityIdVal = watch("specialityId");

  const passScore = useMemo(() => {
    if (!password) return 0;
    let s = 0;
    if (password.length >= 8) s++;
    if (/[A-Z]/.test(password)) s++;
    if (/[0-9]/.test(password)) s++;
    return s;
  }, [password]);

  /* ---------- load lists ---------- */
  const loadInitial = useCallback(async () => {
    try {
      setOffline(false);
      setLoading((l) => ({ ...l, categories: true }));
      setCategories(toArray(await fetchWithRetry("/Categories", { cacheKey: "cache_categories" })));
    } catch (e) {
      setOffline(true);
      setSnack({ open: true, msg: e.message, sev: "error" });
    } finally {
      setLoading((l) => ({ ...l, categories: false }));
    }
    try {
      setLoading((l) => ({ ...l, countries: true }));
      setCountries(toArray(await fetchWithRetry("/Country", { cacheKey: "cache_countries" })));
    } catch (e) {
      setOffline(true);
      setSnack((s) => ({ open: true, msg: s.msg || e.message, sev: "error" }));
    } finally {
      setLoading((l) => ({ ...l, countries: false }));
    }
  }, []);
  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  /* ---------- dependents ---------- */
  useEffect(() => {
    if (!countryId) {
      setCities([]);
      setValue("cityId", "");
      setValue("areaId", "");
      return;
    }
    (async () => {
      try {
        setLoading((l) => ({ ...l, cities: true }));
        setCities(
          toArray(
            await fetchWithRetry(`/City/country/${countryId}`, {
              cacheKey: `cache_cities_${countryId}`,
            })
          )
        );
      } catch (e) {
        setOffline(true);
        setSnack({ open: true, msg: e.message, sev: "error" });
      } finally {
        setLoading((l) => ({ ...l, cities: false }));
      }
    })();
  }, [countryId, setValue]);

  useEffect(() => {
    if (!cityId) {
      setAreas([]);
      setValue("areaId", "");
      return;
    }
    (async () => {
      try {
        setLoading((l) => ({ ...l, areas: true }));
        setAreas(
          toArray(
            await fetchWithRetry(`/Area/city/${cityId}`, {
              cacheKey: `cache_areas_${cityId}`,
            })
          )
        );
      } catch (e) {
        setOffline(true);
        setSnack({ open: true, msg: e.message, sev: "error" });
      } finally {
        setLoading((l) => ({ ...l, areas: false }));
      }
    })();
  }, [cityId, setValue]);

  useEffect(() => {
    if (!categoryId) {
      setSpecialities([]);
      setValue("specialityId", "");
      return;
    }
    (async () => {
      try {
        setLoading((l) => ({ ...l, specialities: true }));
        setSpecialities(
          toArray(
            await fetchWithRetry(`/Specialities?categoryId=${categoryId}`, {
              cacheKey: `cache_specs_${categoryId}`,
            })
          )
        );
      } catch (e) {
        setOffline(true);
        setSnack({ open: true, msg: e.message, sev: "error" });
      } finally {
        setLoading((l) => ({ ...l, specialities: false }));
      }
    })();
  }, [categoryId, setValue]);

  /* ---------- email availability ---------- */
  const checkEmail = useCallback(async (mail) => {
    setEmailStatus("checking");
    try {
      const res = await api.get(`/Coaches/check-email`, { params: { email: mail } });
      setEmailStatus(res?.data?.exists ? "taken" : "free");
    } catch {
      setEmailStatus("idle");
    }
  }, []);

  useEffect(() => {
    if (!email) return;
    clearTimeout(emailTimer.current);
    emailTimer.current = setTimeout(() => checkEmail(email), 500);
    return () => clearTimeout(emailTimer.current);
  }, [email, checkEmail]);

  /* ---------- image (Settings-style) ---------- */
  const dataURLtoBlob = (dataURL) => {
    const [header, base64] = dataURL.split(",");
    const mimeMatch = header.match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : "image/png";
    const binary = atob(base64);
    const array = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) array[i] = binary.charCodeAt(i);
    return new Blob([array], { type: mime });
  };

  useEffect(() => {
    return () => {
      if (croppedImage) URL.revokeObjectURL(croppedImage);
    };
  }, [croppedImage]);

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!/^image\//.test(file.type)) {
      setSnack({ open: true, msg: "Please select an image file", sev: "warning" });
      return;
    }

    try {
      if (croppedImage) URL.revokeObjectURL(croppedImage);
      setCroppedImage(null);
      setCroppedBlob(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);

      const compressed = await imageCompression(file, {
        maxSizeMB: 0.7,
        maxWidthOrHeight: 1200,
        useWebWorker: true,
      });

      const reader = new FileReader();
      reader.onload = () => setImagePreview(reader.result);
      reader.readAsDataURL(compressed);
    } catch {
      setSnack({ open: true, msg: "Image processing failed", sev: "error" });
    }
  };

  const onCropComplete = (_, pixels) => setCroppedAreaPixels(pixels);

  const generateCroppedImage = async () => {
    if (!imagePreview || !croppedAreaPixels) {
      setSnack({ open: true, msg: "Please adjust the crop area first.", sev: "warning" });
      return;
    }
    try {
      const croppedDataURL = await getCroppedImg(imagePreview, croppedAreaPixels);
      if (!croppedDataURL?.startsWith("data:image")) throw new Error("Invalid crop");
      const blob = dataURLtoBlob(croppedDataURL);

      if (croppedImage) URL.revokeObjectURL(croppedImage);

      setCroppedBlob(blob);
      setCroppedImage(URL.createObjectURL(blob));
      setSnack({ open: true, msg: "Profile image ready ✅", sev: "success" });
    } catch {
      setSnack({ open: true, msg: "Cropping failed. Please try again.", sev: "error" });
    }
  };

  /* ---------- account cache (incl. pw, TTL) ---------- */
  const saveAccountCache = useCallback(() => {
    const payload = {
      fullName: (watch("fullName") || "").trim(),
      email: (watch("email") || "").toLowerCase(),
      countryCode: watch("countryCode") || "+971",
      phoneNumber: watch("phoneNumber") || "",
      gender: watch("gender") || "",
      password: watch("password") || "",
      confirmPassword: watch("confirmPassword") || "",
      exp: Date.now() + ACCOUNT_CACHE_TTL_MS,
    };
    localStorage.setItem(ACCOUNT_CACHE_KEY, JSON.stringify(payload));
  }, [watch]);

  const restoreAccountCache = useCallback(() => {
    try {
      const raw = localStorage.getItem(ACCOUNT_CACHE_KEY);
      if (!raw) return;
      const obj = JSON.parse(raw || "{}");
      if (obj.exp && obj.exp < Date.now()) {
        localStorage.removeItem(ACCOUNT_CACHE_KEY);
        return;
      }
      if (obj.fullName) setValue("fullName", obj.fullName);
      if (obj.email) {
        setValue("email", obj.email);
        setPendingEmail(obj.email);
      }
      if (obj.countryCode) setValue("countryCode", obj.countryCode);
      if (obj.phoneNumber) setValue("phoneNumber", obj.phoneNumber);
      if (obj.gender) setValue("gender", obj.gender);
      if (obj.password) setValue("password", obj.password);
      if (obj.confirmPassword) setValue("confirmPassword", obj.confirmPassword);
    } catch {
      /* ignore */
    }
  }, [setValue]);

  useEffect(() => {
    restoreAccountCache();
  }, [restoreAccountCache]);

  /* ---------- wizard ---------- */
  const nextFromAccount = async () => {
    if (loading.sendCode) return;

    const ok = await trigger([
      "fullName",
      "email",
      "password",
      "confirmPassword",
      "countryCode",
      "phoneNumber",
      "gender",
    ]);
    if (!ok) return;

    if (emailStatus === "taken") {
      setSnack({ open: true, msg: "This email is already registered", sev: "warning" });
      return;
    }

    setLoading((l) => ({ ...l, sendCode: true }));
    try {
      saveAccountCache();
      const mail = (watch("email") || "").toLowerCase();
      await requestOtp(mail, 10);
      setPendingEmail(mail);
      setResendCooldown(45);
      setSnack({ open: true, msg: "Code sent. Check your email.", sev: "success" });
      setActiveStep(1);
    } catch (e) {
      setSnack({
        open: true,
        msg: e?.response?.data?.error || e?.message || "Failed to send code.",
        sev: "error",
      });
    } finally {
      setLoading((l) => ({ ...l, sendCode: false }));
    }
  };

  const backToAccount = () => setActiveStep(0);
  const backToVerify = () => setActiveStep(1);

  const nextFromProfile = async () => {
    const ok = await trigger([
      "description",
      "countryId",
      "cityId",
      "areaId",
      "categoryId",
      "specialityId",
      "acceptPolicies",
    ]);
    if (!ok) return;
    if (!croppedBlob) {
      setSnack({ open: true, msg: "Please upload and crop a profile picture", sev: "warning" });
      return;
    }
    setActiveStep(3);
  };

  const fieldStep = (name) => {
    const s0 = ["fullName", "email", "password", "confirmPassword", "countryCode", "phoneNumber", "gender"];
    const s2 = ["description", "countryId", "cityId", "areaId", "categoryId", "specialityId", "acceptPolicies"];
    if (s0.includes(name)) return 0;
    if (s2.includes(name)) return 2;
    return 0;
  };

  const onInvalid = (errs) => {
    const keys = Object.keys(errs || {});
    if (!keys.length) return;
    const first = keys[0];
    setActiveStep(fieldStep(first));
    setSnack({
      open: true,
      msg: errs[first]?.message || "Please complete the required fields.",
      sev: "error",
    });
  };

  /* ---------- submit (Step 4) ---------- */
  const onSubmit = async (values) => {
    const proof = localStorage.getItem(PROOF_KEY);
    if (!proof || !isVerified) {
      setSnack({ open: true, msg: "Email not verified yet.", sev: "warning" });
      return;
    }
    if (!croppedBlob) {
      setSnack({ open: true, msg: "Please upload and crop a profile picture", sev: "warning" });
      return;
    }

    setLoading((l) => ({ ...l, submit: true }));
    try {
      const data = new FormData();
      const phone = `${values.countryCode}${values.phoneNumber}`;
      data.append("PhoneNumber", phone);

      Object.entries(values).forEach(([k, v]) => {
        if (["confirmPassword", "countryCode", "acceptPolicies"].includes(k)) return;
        if (k === "price" && (v === undefined || v === null || v === "")) return;
        data.append(k, v);
      });

      const now = new Date().toISOString();
      data.append("TermsAccepted", "true");
      data.append("TermsVersion", "v1");
      data.append("TermsAcceptedAtUtc", now);

      data.append("PrivacyAccepted", "true");
      data.append("PrivacyVersion", "v1");
      data.append("PrivacyAcceptedAtUtc", now);

      data.append("ConsentLanguage", (navigator.language || "en").slice(0, 2));
      data.append("ClientTimeZone", Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Dubai");
      data.append("UserAgent", navigator.userAgent || "");

      data.append("ProfileImage", croppedBlob, "profile.png");

      await api.post("/Coaches", data, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${proof}` },
      });

      reset();
      setImagePreview(null);
      if (croppedImage) URL.revokeObjectURL(croppedImage);
      setCroppedImage(null);
      setCroppedBlob(null);

      setCities([]);
      setAreas([]);
      setSpecialities([]);
      localStorage.removeItem(PROOF_KEY);
      localStorage.removeItem(ACCOUNT_CACHE_KEY);
      setIsVerified(false);
      setActiveStep(4);
      setSnack({ open: true, msg: "Registration complete!", sev: "success" });
    } catch (error) {
      const msg = error?.response?.data?.error || error?.message || "Registration failed";
      setSnack({ open: true, msg, sev: "error" });
      if (String(msg).toLowerCase().includes("network")) setOffline(true);
    } finally {
      setLoading((l) => ({ ...l, submit: false }));
    }
  };

  const OfflineBanner = () =>
    offline ? (
      <Box className="mb-4 flex items-center gap-2 p-3 rounded-xl bg-orange-50 border border-orange-200">
        <FiWifiOff />
        <span className="text-sm text-orange-800">
          You appear to be offline or the API is unreachable. Showing cached data when available.
        </span>
        <Button size="small" startIcon={<FiRefreshCw />} onClick={loadInitial}>
          Retry
        </Button>
      </Box>
    ) : null;

  const findName = (list, id) => {
    if (!id) return "";
    const it = arr(list).find((x) => String(x.id) === String(id));
    return it ? it.name : "";
  };

  /* ---------- render ---------- */
  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-white via-sky-50 to-sky-100 flex items-center justify-center px-4 py-10">
      <motion.form
        onSubmit={handleSubmit(onSubmit, onInvalid)}
        className="bg-white rounded-2xl shadow-2xl p-8 md:p-10 w-full max-w-3xl"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="text-center mb-6">
          <h2 className="text-3xl font-extrabold text-blue-600">Join PTfinderNow</h2>
          <p className="text-slate-500 mt-1">Create your profile to start getting clients</p>
        </div>

        <OfflineBanner />

        <Stepper activeStep={activeStep} alternativeLabel className="mb-6">
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step 1: Account */}
        {activeStep === 0 && (
          <div className="grid grid-cols-1 gap-4">
            <Controller
              name="fullName"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Full Name"
                  fullWidth
                  value={field.value || ""}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                  error={!!errors.fullName}
                  helperText={errors.fullName?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FaUser />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  value={field.value || ""}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                  error={!!errors.email || emailStatus === "taken"}
                  helperText={
                    errors.email?.message ||
                    (emailStatus === "checking"
                      ? "Checking email…"
                      : emailStatus === "taken"
                      ? "This email is already registered"
                      : "")
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FaEnvelope />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <PasswordField
                  label="Password"
                  field={field}
                  error={errors.password}
                  helperText={errors.password?.message}
                />
              )}
            />

            <div className="h-1 rounded bg-slate-200">
              <div
                className={`h-1 rounded ${
                  ["w-1/5 bg-red-300", "w-2/5 bg-yellow-300", "w-3/5 bg-yellow-400", "w-4/5 bg-green-400"][passScore]
                }`}
              />
            </div>

            <Controller
              name="confirmPassword"
              control={control}
              render={({ field }) => (
                <PasswordField
                  label="Confirm Password"
                  field={field}
                  error={errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                />
              )}
            />

            <div className="flex gap-3 items-start">
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel id="ccode">Code</InputLabel>
                <Select
                  labelId="ccode"
                  label="Code"
                  value={countryCodeVal || "+971"}
                  onChange={(e) => setValue("countryCode", e.target.value, { shouldValidate: true })}
                >
                  {countryCodes.map((c, i) => (
                    <MenuItem key={i} value={c.code}>
                      {c.code} ({c.label})
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText error={!!errors.countryCode}>{errors.countryCode?.message}</FormHelperText>
              </FormControl>

              <Controller
                name="phoneNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Phone Number"
                    fullWidth
                    value={field.value || ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (/^\d{0,9}$/.test(v)) field.onChange(v);
                    }}
                    onBlur={field.onBlur}
                    inputRef={field.ref}
                    inputProps={{ inputMode: "numeric", maxLength: 9 }}
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber?.message}
                  />
                )}
              />
            </div>

            <FormControl fullWidth error={!!errors.gender}>
              <InputLabel id="gender">Gender</InputLabel>
              <Select
                labelId="gender"
                label="Gender"
                value={genderVal || ""}
                onChange={(e) => setValue("gender", e.target.value, { shouldValidate: true })}
              >
                <MenuItem value="">Gender</MenuItem>
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
              </Select>
              <FormHelperText>{errors.gender?.message}</FormHelperText>
            </FormControl>

            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="contained"
                onClick={nextFromAccount}
                disabled={loading.sendCode || emailStatus === "checking"}
                startIcon={loading.sendCode ? <CircularProgress size={18} /> : null}
              >
                {loading.sendCode ? "Sending..." : "Send code"}
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Verify code (OTP) */}
        {activeStep === 1 && (
          <div className="grid grid-cols-1 gap-6">
            <div className="rounded-xl border border-slate-200 p-5 bg-slate-50">
              <h3 className="text-lg font-semibold mb-1">Verify your email</h3>
              <p className="text-sm text-slate-600">
                We sent a 6-digit code to <strong>{pendingEmail || "your email"}</strong>. Enter it below.
              </p>

              <div className="flex items-center gap-3 mt-3">
                <TextField
                  label="6-digit code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputProps={{ inputMode: "numeric" }}
                />
                <Button
                  variant="contained"
                  disabled={otp.length !== 6}
                  onClick={async () => {
                    try {
                      const mail = (pendingEmail || watch("email") || "").toLowerCase();
                      const { emailProof } = await verifyOtp(mail, otp);
                      localStorage.setItem(PROOF_KEY, emailProof);
                      setIsVerified(true);
                      setSnack({ open: true, msg: "Email verified.", sev: "success" });
                      setActiveStep(2);
                    } catch (e) {
                      setSnack({ open: true, msg: e?.response?.data?.error || "Invalid code.", sev: "error" });
                    }
                  }}
                >
                  Verify
                </Button>
              </div>

              <div className="flex gap-2 mt-4 flex-wrap">
                <Button variant="outlined" onClick={backToAccount}>
                  Back
                </Button>

                <Tooltip title={resendCooldown > 0 ? `You can resend in ${resendCooldown}s` : "Send the code again"}>
                  <span>
                    <Button
                      variant="outlined"
                      onClick={async () => {
                        if (!pendingEmail || resendCooldown > 0) return;
                        try {
                          setLoading((l) => ({ ...l, resend: true }));
                          await requestOtp(pendingEmail, 10);
                          setResendCooldown(45);
                          setSnack({ open: true, msg: "Code resent.", sev: "success" });
                        } catch (e) {
                          setSnack({
                            open: true,
                            msg: e?.response?.data?.error || "Couldn’t resend code.",
                            sev: "error",
                          });
                        } finally {
                          setLoading((l) => ({ ...l, resend: false }));
                        }
                      }}
                      disabled={resendCooldown > 0 || loading.resend}
                      startIcon={loading.resend ? <CircularProgress size={16} /> : <FiRefreshCw />}
                    >
                      {resendCooldown > 0 ? `Resend (${resendCooldown}s)` : "Resend code"}
                    </Button>
                  </span>
                </Tooltip>
              </div>
            </div>

            <div className="text-slate-500 text-xs">
              Didn’t get it? Check <strong>Spam/Junk</strong>. If it still doesn’t arrive, contact{" "}
              <a href="mailto:info@ptfindernow.com" className="underline">
                info@ptfindernow.com
              </a>
              .
            </div>
          </div>
        )}

        {/* Step 3: Profile */}
        {activeStep === 2 && (
          <div className="grid grid-cols-1 gap-4">
            {!isVerified && <Alert severity="warning">Please verify your email first.</Alert>}

            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Description"
                  multiline
                  rows={4}
                  fullWidth
                  inputProps={{ maxLength: 1000 }}
                  value={field.value || ""}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                  error={!!errors.description}
                  helperText={errors.description?.message || `${(field.value || "").length}/1000`}
                />
              )}
            />

            <Controller
              name="price"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Price per Session (AED, optional)"
                  type="number"
                  inputProps={{ min: 0, step: 5 }}
                  fullWidth
                  value={field.value || ""}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FaMoneyBillWave />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <FormControl fullWidth error={!!errors.countryId}>
              <InputLabel id="country">Country</InputLabel>
              <Select
                labelId="country"
                label="Country"
                value={countryIdVal || ""}
                onChange={(e) => setValue("countryId", String(e.target.value), { shouldValidate: true })}
              >
                <MenuItem value="">Select Country</MenuItem>
                {loading.countries ? (
                  <MenuItem disabled>
                    <CircularProgress size={16} sx={{ mr: 1 }} /> Loading…
                  </MenuItem>
                ) : (
                  arr(countries).map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>
                      {c.name}
                    </MenuItem>
                  ))
                )}
              </Select>
              <FormHelperText>{errors.countryId?.message}</FormHelperText>
            </FormControl>

            <FormControl fullWidth error={!!errors.cityId}>
              <InputLabel id="city">City</InputLabel>
              <Select
                labelId="city"
                label="City"
                value={cityIdVal || ""}
                onChange={(e) => setValue("cityId", String(e.target.value), { shouldValidate: true })}
              >
                <MenuItem value="">Select City</MenuItem>
                {loading.cities ? (
                  <MenuItem disabled>
                    <CircularProgress size={16} sx={{ mr: 1 }} /> Loading…
                  </MenuItem>
                ) : (
                  arr(cities).map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>
                      {c.name}
                    </MenuItem>
                  ))
                )}
              </Select>
              <FormHelperText>{errors.cityId?.message}</FormHelperText>
            </FormControl>

            <FormControl fullWidth error={!!errors.areaId}>
              <InputLabel id="area">Area</InputLabel>
              <Select
                labelId="area"
                label="Area"
                value={areaIdVal || ""}
                onChange={(e) => setValue("areaId", String(e.target.value), { shouldValidate: true })}
              >
                <MenuItem value="">Select Area</MenuItem>
                {loading.areas ? (
                  <MenuItem disabled>
                    <CircularProgress size={16} sx={{ mr: 1 }} /> Loading…
                  </MenuItem>
                ) : (
                  arr(areas).map((a) => (
                    <MenuItem key={a.id} value={String(a.id)}>
                      {a.name}
                    </MenuItem>
                  ))
                )}
              </Select>
              <FormHelperText>{errors.areaId?.message}</FormHelperText>
            </FormControl>

            <FormControl fullWidth error={!!errors.categoryId}>
              <InputLabel id="category">Category</InputLabel>
              <Select
                labelId="category"
                label="Category"
                value={categoryIdVal || ""}
                onChange={(e) => setValue("categoryId", String(e.target.value), { shouldValidate: true })}
              >
                <MenuItem value="">Select Category</MenuItem>
                {loading.categories ? (
                  <MenuItem disabled>
                    <CircularProgress size={16} sx={{ mr: 1 }} /> Loading…
                  </MenuItem>
                ) : (
                  arr(categories).map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>
                      {c.name}
                    </MenuItem>
                  ))
                )}
              </Select>
              <FormHelperText>{errors.categoryId?.message}</FormHelperText>
            </FormControl>

            <FormControl fullWidth error={!!errors.specialityId}>
              <InputLabel id="speciality">Speciality</InputLabel>
              <Select
                labelId="speciality"
                label="Speciality"
                value={specialityIdVal || ""}
                onChange={(e) => setValue("specialityId", String(e.target.value), { shouldValidate: true })}
              >
                <MenuItem value="">Select Speciality</MenuItem>
                {loading.specialities ? (
                  <MenuItem disabled>
                    <CircularProgress size={16} sx={{ mr: 1 }} /> Loading…
                  </MenuItem>
                ) : (
                  arr(specialities).map((s) => (
                    <MenuItem key={s.id} value={String(s.id)}>
                      {s.name}
                    </MenuItem>
                  ))
                )}
              </Select>
              <FormHelperText>{errors.specialityId?.message}</FormHelperText>
            </FormControl>

            {/* Image upload & crop */}
            <div className="mt-2">
              <div className="border rounded-xl p-3 bg-slate-50">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-slate-700 mb-1">Profile photo</label>
                    <p className="text-xs text-slate-500 mb-2">
                      Upload a clear photo of your face. Crop it to a nice circle.
                    </p>
                    <input type="file" accept="image/*" onChange={handleImageChange} className="block text-xs" />
                  </div>

                  {croppedBlob && (
                    <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                      Image ready ✅
                    </div>
                  )}
                </div>

                <AnimatePresence>
                  {imagePreview && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      className="mt-4"
                    >
                      <div className="relative w-full h-64 rounded-2xl overflow-hidden bg-black/5">
                        <Cropper
                          image={imagePreview}
                          crop={crop}
                          zoom={zoom}
                          aspect={1}
                          cropShape="round"
                          showGrid={false}
                          onCropChange={setCrop}
                          onZoomChange={setZoom}
                          onCropComplete={onCropComplete}
                        />
                      </div>

                      <div className="flex items-center gap-2 mt-3 text-xs">
                        <span className="text-slate-500 min-w-[70px]">Zoom</span>
                        <input
                          type="range"
                          min={0.8}
                          max={3}
                          step={0.1}
                          value={zoom}
                          onChange={(e) => setZoom(Number(e.target.value))}
                          className="w-full"
                        />
                        <Button
                          variant="contained"
                          color="secondary"
                          onClick={generateCroppedImage}
                          sx={{ ml: 1, whiteSpace: "nowrap" }}
                        >
                          Save crop
                        </Button>
                      </div>

                      {croppedImage && (
                        <div className="mt-3 flex items-center gap-3">
                          <img
                            src={croppedImage}
                            alt="Cropped Preview"
                            className="w-20 h-20 rounded-full border border-slate-200 object-cover"
                          />
                          <div className="text-xs text-slate-500">Preview (this will be uploaded)</div>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* ✅ Checkbox via Controller (Fix #185) */}
            <Controller
              name="acceptPolicies"
              control={control}
              render={({ field }) => (
                <>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label={
                      <span>
                        I accept the{" "}
                        <a className="text-blue-600 underline" href="/TermsAndConditions" target="_blank" rel="noreferrer">
                          Terms & Conditions
                        </a>{" "}
                        and{" "}
                        <a className="text-blue-600 underline" href="/PrivacyPolicy" target="_blank" rel="noreferrer">
                          Privacy Policy
                        </a>
                        .
                      </span>
                    }
                  />
                  <FormHelperText error={!!errors.acceptPolicies}>
                    {errors.acceptPolicies?.message}
                  </FormHelperText>
                </>
              )}
            />

            <div className="flex justify-between gap-3 pt-2">
              <Button variant="outlined" onClick={backToVerify}>
                Back
              </Button>
              <Button variant="contained" onClick={nextFromProfile} disabled={!isVerified}>
                Continue
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Register (Review & Submit) */}
        {activeStep === 3 && (
          <div className="grid grid-cols-1 gap-6">
            <div className="rounded-xl border border-slate-200 p-5 bg-white">
              <h3 className="text-lg font-semibold mb-1">Review your details</h3>
              <p className="text-sm text-slate-600 mb-4">
                Please confirm the information below before creating your account.
              </p>

              <div className="mb-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Account</h4>
                <ReviewRow label="Full name" value={watch("fullName")} />
                <ReviewRow label="Email" value={watch("email")} />
                <ReviewRow label="Phone" value={`${watch("countryCode")}${watch("phoneNumber")}`} />
                <ReviewRow label="Gender" value={watch("gender")} />
              </div>

              <div className="mb-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Profile</h4>
                <ReviewRow label="Description" value={watch("description")} />
                <ReviewRow label="Price (AED)" value={watch("price")} />
                <ReviewRow label="Country" value={findName(countries, watch("countryId"))} />
                <ReviewRow label="City" value={findName(cities, watch("cityId"))} />
                <ReviewRow label="Area" value={findName(areas, watch("areaId"))} />
                <ReviewRow label="Category" value={findName(categories, watch("categoryId"))} />
                <ReviewRow label="Speciality" value={findName(specialities, watch("specialityId"))} />
              </div>

              <div className="mb-2">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Profile photo</h4>
                {croppedImage ? (
                  <img
                    src={croppedImage}
                    alt="Profile"
                    className="w-24 h-24 rounded-full border border-slate-200 object-cover"
                  />
                ) : (
                  <span className="text-slate-500">No photo</span>
                )}
              </div>

              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outlined" onClick={() => setActiveStep(2)}>
                  Back to profile
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit(onSubmit, onInvalid)}
                  disabled={!isVerified || !croppedBlob || loading.submit}
                >
                  {loading.submit ? "Registering..." : "Register"}
                </Button>
              </div>

              {!isVerified && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Your email must be verified before you can register.
                </Alert>
              )}
            </div>
          </div>
        )}

        {/* Step 5: Done */}
        {activeStep === 4 && (
          <div className="text-center space-y-3">
            <h3 className="text-2xl font-semibold text-green-600">You’re all set! 🎉</h3>
            <p className="text-slate-600">
              Your coach account has been created. You can now sign in and finish your profile.
            </p>
            <div className="pt-2">
              <Button variant="contained" onClick={() => (window.location.href = `${WEB_BASE_URL}/login`)}>
                Go to Login
              </Button>
            </div>
          </div>
        )}
      </motion.form>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.sev}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </div>
  );
}

/* ---------- Small subcomponents ---------- */
function PasswordField({ label, field, error, helperText }) {
  const [show, setShow] = useState(false);

  return (
    <TextField
      label={label}
      type={show ? "text" : "password"}
      fullWidth
      value={field.value || ""}
      onChange={field.onChange}
      onBlur={field.onBlur}
      inputRef={field.ref}
      error={!!error}
      helperText={helperText}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <FaLock />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              onClick={() => setShow((s) => !s)}
              edge="end"
              aria-label={show ? "Hide password" : "Show password"}
            >
              {show ? <FiEyeOff /> : <FiEye />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
}

function ReviewRow({ label, value }) {
  return (
    <div className="flex items-start justify-between border-b border-slate-200 py-2">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-800 text-right">{value || "—"}</span>
    </div>
  );
}
